import React, { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash/debounce';

import { searchGiphyGifs } from 'Shared/api/common';
import Spinner from 'Shared/components/Spinner';
import SearchInput from 'Shared/components/SearchInput/SearchInput';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import './GifPicker.scss';

const defaultSearchQueryString = 'great job';
const defaultGifWidth = 100;
const defaultGifLimit = 25;

type Props = {
  searchInputClassName?: string;
  gifListClassName?: string;
  gifLimit?: number;
  initialSearchQueryString?: string;
  gifWidth?: number;
  onGifClick: Function;
  searchStr?: string;
  searchInput?: boolean;
  onFocus?: Function;
  onBlur?: Function;
};

function GifPicker({
  onGifClick,
  searchInputClassName,
  gifLimit = defaultGifLimit,
  initialSearchQueryString = defaultSearchQueryString,
  gifWidth = defaultGifWidth,
  gifListClassName = '',
  searchStr,
  searchInput = true,
  onFocus,
  onBlur,
}: Props) {
  const inputRef = useRef(null);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [gifs, setGifs] = useState([]);
  const { tr } = useTranslation();

  const sendQuery = useCallback(async value => {
    let response;
    if (value) {
      response = await searchGiphyGifs({ searchValue: value, limit: gifLimit });
    } else {
      response = await searchGiphyGifs({ searchValue: initialSearchQueryString, limit: gifLimit });
    }
    if (response && response.status === 200) {
      setGifs(response.data.data.map(({ images }) => images));
      if (onFocus) setTimeout(onFocus);
      if (onBlur) setTimeout(onBlur);
    }
  }, []);

  const debouncedSendQuery = useCallback(debounce(sendQuery, 300), []);

  const searchValueChange = useCallback(
    async event => {
      const value = event.target.value;
      setSearchValue(value);
      setIsLoading(true);
      await debouncedSendQuery(value);
      setIsLoading(false);
    },
    [debouncedSendQuery]
  );

  useEffect(() => {
    if (searchStr) {
      searchValueChange({ target: { value: searchStr } });
    }
  }, [searchStr]);

  useEffect(() => {
    async function fetchDefaultGifs() {
      setIsLoading(true);
      const response = await searchGiphyGifs({ searchValue: initialSearchQueryString, limit: gifLimit });
      if (response && response.status === 200) setGifs(response.data.data.map(({ images }) => images));
      setIsLoading(false);
    }
    fetchDefaultGifs();
    if (inputRef?.current) inputRef.current.focus();
  }, []);

  return (
    <div className="gif-picker">
      {searchInput ? (
        <SearchInput
          inputRef={inputRef}
          className={searchInputClassName}
          value={searchValue}
          onChange={searchValueChange}
          placeholder={tr('perform.gif_picker.search.placeholder')}
        />
      ) : null}
      <div className={`gif-picker-list d-flex flex-wrap border justify-content-between scrollbar ${gifListClassName}`}>
        {isLoading ? (
          <Spinner activeIf />
        ) : (
          <>
            {gifs.map(gifItem => (
              <div key={gifItem.fixed_width.url} className="m-2 cursor-pointer" onClick={() => onGifClick(gifItem)}>
                <img src={gifItem.fixed_width.url} alt={gifItem.fixed_width.url} width={gifWidth} />
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export default GifPicker;
